import { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Box,
  InputSearch,
  mainTheme,
  Modal,
  ModalContext,
  SearchContext,
  Spinner,
  toast,
} from 'dash-ui';

import {
  Amenity,
  GetAmenitiesDocument,
  GetAmenitiesForManageDocument,
  GetPropertyByIdDocument,
  useGetAmenitiesForManageQuery,
  useSetAmenitiesToPropertyMutation,
} from 'gql/generated.graphql';

import AmenityListItem from 'containers/properties/PropertyFeaturesAndAmenities/components/AmenityListItem/AmenityListItem';
import { PropertyFeaturesAndAmenityTabModalsEnum } from 'containers/properties/PropertyFeaturesAndAmenities/constants';

const AmenitiesBox = styled(Box)`
  border-top: 1px solid ${mainTheme.colors.n30};
  border-bottom: 1px solid ${mainTheme.colors.n30};
  max-height: 480px;
  overflow-y: scroll;
  margin-top: 15px;

  // Hide scrollbar
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;

function ManageAmenitiesModal() {
  const { searchSlug, setSearchSlug } = useContext(SearchContext);
  const [selectedAmenitiesIds, setSelectedAmenitiesIds] = useState<string[]>(
    []
  );
  const { toggleModal, setPayload, isOpenCurrent, payload } =
    useContext(ModalContext);
  const isOpen =
    !!isOpenCurrent[
      PropertyFeaturesAndAmenityTabModalsEnum.MANAGE_AMENITIES_MODAL
    ];

  const { t: tPropertyPage } = useTranslation('pages', {
    keyPrefix: 'propertyPage',
  });
  const { t: tCommonLabels } = useTranslation('common', {
    keyPrefix: 'labels',
  });

  const { data: result, loading: amenitiesGetLoading } =
    useGetAmenitiesForManageQuery({
      variables: {
        pagination: {
          allowLimit: false,
        },
        filter: {
          propertyId: payload?.property?._id,
          ...(searchSlug.length > 2 && { searchByName: searchSlug }),
        },
        sort: [{ fieldName: 'name', sortOrder: 1 }],
      },
      skip: !payload?.property?._id,
      fetchPolicy: 'network-only',
    });
  const data = useMemo(() => {
    let sortedData = [...(result?.getAmenitiesForManage?.collection || [])];
    return sortedData.sort((a, b) => {
      // First, compare the 'checked' property (true comes before false)
      if (
        selectedAmenitiesIds.includes(a._id) ===
        selectedAmenitiesIds.includes(b._id)
      ) {
        // If 'checked' values are the same, compare 'name' alphabetically
        return a.name.localeCompare(b.name);
      }

      // If 'a' is checked and 'b' is not, 'a' comes first
      return selectedAmenitiesIds.includes(a._id) ? -1 : 1;
    });
  }, [result]);

  const onCloseModal = (resetForm?: boolean) => {
    if (resetForm && payload?.property?.amenityIds) {
      //reset selected back to original
      setSelectedAmenitiesIds(payload?.property?.amenityIds);
    }
    setPayload(null);
    toggleModal(
      false,
      PropertyFeaturesAndAmenityTabModalsEnum.MANAGE_AMENITIES_MODAL
    );
  };

  const [setAmenitiesToPropertyMutation, { loading }] =
    useSetAmenitiesToPropertyMutation({
      onCompleted: () => onCloseModal(),
      onError: (error) => toast.error(error.message),
      refetchQueries: [
        GetAmenitiesForManageDocument,
        GetAmenitiesDocument,
        GetPropertyByIdDocument,
      ],
    });

  const onCheckboxChange = (isChecked: boolean, amenityId: string) => {
    if (isChecked) {
      setSelectedAmenitiesIds((prevState) => [...prevState, amenityId]);
    } else {
      setSelectedAmenitiesIds((prevState) =>
        prevState.filter((id) => id !== amenityId)
      );
    }
  };

  const onClickUpdate = () => {
    setAmenitiesToPropertyMutation({
      variables: {
        input: {
          propertyId: payload?.property?._id,
          amenityIds: selectedAmenitiesIds,
        },
      },
    });
  };

  const onEditIconClick = (amenity: Amenity) => {
    setPayload({
      nextModalToOpen:
        PropertyFeaturesAndAmenityTabModalsEnum.MANAGE_AMENITIES_MODAL,
      property: payload?.property,
      amenity,
    });
    toggleModal(
      false,
      PropertyFeaturesAndAmenityTabModalsEnum.MANAGE_AMENITIES_MODAL
    );
    toggleModal(
      true,
      PropertyFeaturesAndAmenityTabModalsEnum.EDIT_AMENITY_NAME_IN_PROPERTY_MODAL
    );
  };

  useEffect(() => {
    if (payload?.property?.amenityIds) {
      setSelectedAmenitiesIds(payload?.property?.amenityIds);
    }
  }, [payload?.property]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => onCloseModal(true)}
      title={tPropertyPage`manageAmenities` as string}
      submitButton={{
        title: tCommonLabels`update`,
        isLoading: loading,
        onClick: () => onClickUpdate(),
      }}
      cancelButton={{
        title: tCommonLabels`cancel`,
        onClick: onCloseModal,
      }}
    >
      {/* TODO: Move it to infinitive scroll */}
      <Box mt={5} mb={7} w="100%" pos="relative">
        <InputSearch
          w="100%"
          placeholder={tPropertyPage`searchAmenities` || ''}
          mb={3}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setSearchSlug(e.target.value)
          }
          onClear={() => setSearchSlug('')}
        />
        <AmenitiesBox w="100%">
          {data.map((amenity) => (
            <AmenityListItem
              key={amenity._id}
              amenity={amenity as Amenity}
              isCheckboxChecked={selectedAmenitiesIds.includes(amenity._id)}
              onCheckboxChange={(isChecked) => {
                if (amenity.isPrimary && !isChecked) {
                  const duplicateIds = data
                    .filter((a) => a.primaryAmenityId === amenity._id)
                    .map((a) => a._id);
                  setSelectedAmenitiesIds((prevState) =>
                    prevState.filter(
                      (id) => ![amenity._id, ...duplicateIds].includes(id)
                    )
                  );
                } else {
                  onCheckboxChange(isChecked, amenity._id);
                }
              }}
              disabled={
                amenity.isPrimary
                  ? false
                  : selectedAmenitiesIds.includes(amenity.primaryAmenityId)
                  ? false
                  : true
              }
              onEditIconClick={() => onEditIconClick(amenity as Amenity)}
              setSelectedAmenitiesIds={setSelectedAmenitiesIds}
            />
          ))}
        </AmenitiesBox>
        {amenitiesGetLoading && <Spinner fullContent />}
      </Box>
    </Modal>
  );
}

export default ManageAmenitiesModal;
